import {
  type FunctionComponent,
  type Ref,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { Button, type Theme, useTheme } from '@dr.pogodin/react-utils';

import { useImageUrl } from 'hooks';

import {
  ALLOWED_IMAGE_EXT,
  type ImageAssetT,
  P_API_ASSETS,
  isAllowedFile,
} from 'utils';

import IconTrash from 'assets/images/icon-trash-16px.svg';

import buttonTheme from 'themes/Button/admin/icon.scss';

import { dragging } from '../Library/Item';

import S from './default.theme.scss';

type ThemeT = Theme<'container' | 'highlighted'>;

export type RefT = {
  openPicker: () => void;
};

type PropsT = {
  image: File | ImageAssetT | null | undefined;
  noDelete?: boolean;
  ref?: Ref<RefT>;
  setImage: (image: File | ImageAssetT | null) => void;
  theme?: ThemeT;
};

export const AssetPreview: FunctionComponent<PropsT> = ({
  image,
  noDelete,
  ref,
  setImage,
  theme,
}) => {
  const composed = useTheme('AssetPreview', S, theme);

  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    openPicker: () => inputRef.current?.click(),
  }), []);

  const fileImageUrl = useImageUrl(image);

  const [highlighted, setHighlighted] = useState(false);

  let containerClass = composed.container ?? '';
  if (highlighted) containerClass += ` ${composed.highlighted ?? ''}`;

  let url: string | undefined;
  if (image instanceof File) url = fileImageUrl;
  else if (image) url = `${P_API_ASSETS}/${image.id}/file?s=360`;

  return (
    <>
      <div
        className={containerClass}
        onClick={() => {
          inputRef.current?.click();
        }}
        onDragLeave={() => {
          setHighlighted(false);
        }}
        onDragOver={(e) => {
          e.preventDefault();

          const { data } = dragging;
          if (data) {
            if (
              data instanceof File
                ? data.type.startsWith('image/')
                : data.kind === 'IMAGE'
            ) setHighlighted(true);
          } else {
            const [item] = e.dataTransfer.items;
            if (item?.type.startsWith('image/')) setHighlighted(true);
          }
        }}
        onDrop={(e) => {
          e.preventDefault();
          const { data } = dragging;
          if (data) {
            if (data instanceof File) {
              if (data.type.startsWith('image/')) setImage(data);
            } else if (data.kind === 'IMAGE') setImage(data);
          } else {
            const [item] = e.dataTransfer.items;
            if (item?.kind === 'file' && item.type.startsWith('image/')) {
              setImage(item.getAsFile());
            }
          }
          setHighlighted(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') inputRef.current?.click();
        }}
        role="button"
        tabIndex={0}
      >
        +
        {url ? (
          <div styleName="preview">
            <img alt="Preview" src={url} />
          </div>
        ) : null}
        {image && !noDelete ? (
          <div styleName="buttons">
            <Button
              onClick={(e) => {
                setImage(null);
                e.stopPropagation();
              }}
              theme={buttonTheme}
            >
              <IconTrash />
            </Button>
          </div>
        ) : null}
      </div>
      <input
        accept={ALLOWED_IMAGE_EXT.join(',')}
        onChange={(e) => {
          if (e.target.files) {
            const [file] = e.target.files;

            if (!file || e.target.files.length !== 1) {
              throw Error('Expected a single file');
            }

            if (inputRef.current) inputRef.current.value = '';
            const allowed = isAllowedFile(file, ['IMAGE']);
            if (!allowed.value) {
              // eslint-disable-next-line no-alert
              alert(allowed.reason);
              return;
            }

            setImage(file);
          }
        }}
        ref={inputRef}
        styleName="hidden"
        type="file"
      />
    </>
  );
};
